import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Target, ArrowRight } from "lucide-react";
import { visionMission, stats, aboutImages } from "../mock/mock";
import { Eyebrow } from "./SectionHeading";
import SmartImage from "./SmartImage";

function Counter({ value, prefix = "", suffix = "" }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const done = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !done.current) {
          done.current = true;
          const dur = 1500;
          const start = performance.now();
          const tick = (now) => {
            const p = Math.min((now - start) / dur, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setN(Math.round(value * eased));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [value]);

  return (
    <span ref={ref}>
      {prefix}
      {n.toLocaleString("en-IN")}
      {suffix}
    </span>
  );
}

export default function CampusLife() {
  return (
    <section id="campus" className="relative overflow-hidden py-24 lg:py-32" style={{ background: "var(--teal-900)" }}>
      <div className="grain absolute inset-0" />
      <div
        className="absolute -left-40 top-1/4 h-[420px] w-[420px] rounded-full"
        style={{ background: "radial-gradient(circle, rgba(192,138,46,.20), transparent 70%)" }}
      />

      <div className="relative mx-auto max-w-[1280px] px-5 lg:px-6">
        <div className="max-w-3xl" data-reveal="">
          <Eyebrow tone="light">Campus Life</Eyebrow>
          <h2
            className="font-display mt-5 text-[2.1rem] leading-[1.1] tracking-tight sm:text-[3rem]"
            style={{ color: "#FBF7F0", fontWeight: 300 }}
          >
            The KPR Academy — where learning lives outdoors, on stage, and in the lab
          </h2>
        </div>

        <div className="mt-16 grid gap-8 lg:grid-cols-[1.05fr_1fr] lg:gap-14">
          {/* Vision & Mission */}
          <div className="space-y-6">
            {[
              { icon: Eye, title: "Vision", body: visionMission.vision, list: null },
              { icon: Target, title: "Mission", body: null, list: visionMission.mission },
            ].map((b, i) => (
              <div
                key={b.title}
                data-reveal=""
                style={{
                  transitionDelay: `${i * 120}ms`,
                  background: "rgba(251,247,240,.05)",
                  border: "1px solid rgba(235,214,168,.18)",
                  backdropFilter: "blur(6px)",
                }}
                className="rounded-2xl p-8"
              >
                <div className="flex items-center gap-4">
                  <span
                    className="grid h-12 w-12 place-items-center rounded-xl"
                    style={{ background: "rgba(235,214,168,.14)", color: "var(--gold-200)" }}
                  >
                    <b.icon size={22} strokeWidth={1.6} />
                  </span>
                  <h3 className="eyebrow" style={{ color: "var(--gold-200)", fontSize: "0.8rem" }}>
                    {b.title}
                  </h3>
                </div>

                {b.body && (
                  <p className="mt-6 text-[0.98rem] leading-relaxed" style={{ color: "rgba(251,247,240,.8)" }}>
                    {b.body}
                  </p>
                )}
                {b.list && (
                  <ul className="mt-6 space-y-4">
                    {b.list.map((m) => (
                      <li key={m} className="flex gap-3 text-[0.95rem] leading-relaxed" style={{ color: "rgba(251,247,240,.8)" }}>
                        <span className="mt-2 h-[6px] w-[6px] shrink-0 rotate-45" style={{ background: "var(--gold-400)" }} />
                        {m}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          {/* Campus image + copy */}
          <div data-reveal="" style={{ transitionDelay: "160ms" }}>
            <div className="overflow-hidden rounded-2xl">
              <SmartImage
                src={aboutImages.campus}
                fallback={aboutImages.campusFallback}
                alt="KPR Academy campus"
                className="h-[300px] w-full object-cover hover:scale-105 lg:h-[360px]"
                style={{ transition: "transform .8s cubic-bezier(.22,1,.36,1), opacity .6s ease" }}
              />
            </div>
            <p className="mt-8 text-[1.02rem] leading-relaxed" style={{ color: "rgba(251,247,240,.82)" }}>
              At The KPR Academy, we empower learners to explore, innovate, and excel in an environment
              built for growth.
            </p>
            <p className="mt-4 text-[1.02rem] font-semibold leading-relaxed" style={{ color: "var(--gold-200)" }}>
              Our educational philosophy focuses on conceptual clarity, creative expression, and character building.
            </p>
            <Link
              to="/campus#campus"
              className="group mt-8 inline-flex items-center gap-3 text-[0.88rem] font-semibold"
              style={{ color: "#FBF7F0" }}
            >
              More about Campus Life
              <span
                className="grid h-9 w-9 place-items-center rounded-full"
                style={{ border: "1px solid rgba(235,214,168,.4)", transition: "background .3s ease" }}
              >
                <ArrowRight size={15} className="group-hover:translate-x-0.5" style={{ transition: "transform .3s ease" }} />
              </span>
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div
          className="mt-20 grid grid-cols-2 gap-y-10 rounded-2xl py-12 lg:grid-cols-4"
          style={{ borderTop: "1px solid rgba(235,214,168,.2)" }}
          data-reveal=""
        >
          {stats.map((s) => (
            <div key={s.label} className="px-4 text-center">
              <p className="font-display text-[2.2rem] leading-none sm:text-[3rem]" style={{ color: "var(--gold-200)", fontWeight: 300 }}>
                <Counter value={s.value} prefix={s.prefix} suffix={s.suffix} />
              </p>
              <p className="mt-3 text-[0.78rem] tracking-[0.14em] uppercase" style={{ color: "rgba(251,247,240,.6)" }}>
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
